import WeeklyRevenue from "views/admin/default/components/WeeklyRevenue";
import TotalSpent from "views/admin/default/components/TotalSpent";
import PieChartCard from "views/admin/default/components/PieChartCard";
import EstimatedHours from "views/admin/default/components/EstimatedHours";
import ActualHours from "views/admin/default/components/ActualHours";
import { MdPeople, MdAttachMoney } from "react-icons/md";
import Widget from "components/widget/Widget";
import DailyTraffic from "views/admin/default/components/DailyTraffic";
import useDashboardData from "hooks/useDashboardData";
import usePermission from "hooks/usePermission";
import { useNavigate } from "react-router-dom";

// Loader overlay: blur dashboard area, animated spinner, always centered in viewport (screen)
const LoaderOverlay = () => (
  <>
    {/* Blur only, no background color, covers dashboard area */}
    <div className="absolute inset-0 z-30 pointer-events-none">
      <div className="absolute inset-0 w-full h-full rounded-2xl backdrop-blur-2xl" style={{ WebkitBackdropFilter: 'blur(18px)', backdropFilter: 'blur(18px)' }}></div>
    </div>
    {/* Loader always centered in viewport (screen) */}
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div className="flex flex-col items-center">
        <span className="relative flex h-28 w-28 mb-7">
          <span className="absolute inline-flex h-full w-full rounded-full bg-gradient-to-tr from-brand-500 via-blue-400 to-brand-400 opacity-30 animate-pulse"></span>
          <svg className="animate-spin h-28 w-28 text-brand-500" viewBox="0 0 50 50">
            <circle className="opacity-20" cx="25" cy="25" r="20" stroke="currentColor" strokeWidth="6" fill="none" />
            <circle className="stroke-[6]" cx="25" cy="25" r="20" stroke="url(#loader-gradient)" strokeWidth="6" fill="none" strokeDasharray="100" strokeDashoffset="60" />
            <defs>
              <linearGradient id="loader-gradient" x1="0" y1="0" x2="50" y2="50" gradientUnits="userSpaceOnUse">
                <stop stopColor="#6366F1" />
                <stop offset="1" stopColor="#38BDF8" />
              </linearGradient>
            </defs>
          </svg>
        </span>
        <span className="text-brand-500 font-extrabold text-2xl tracking-wide drop-shadow text-center bg-white/60 dark:bg-gray-900/60 px-6 py-2 rounded-xl border border-white/30 dark:border-gray-700/30 shadow-lg pointer-events-auto">
          Loading your dashboard...
        </span>
      </div>
    </div>
  </>
);


const Dashboard = () => {
  const { data, loading } = useDashboardData();
  const { getAccessible } = usePermission();
  const accessible = getAccessible();
  const navigate = useNavigate();

  if (!loading && Array.isArray(accessible) && accessible.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-6 text-center">
          <h2 className="mb-2 text-xl font-semibold text-yellow-800">No access assigned</h2>
          <p className="text-sm text-yellow-700">You don't have permissions to access any modules. Please contact your administrator to request access.</p>
        </div>
      </div>
    );
  }
  // const { data, loading, error } = useDashboardData();

  // Helper for formatting INR
  const formatINR = (value) => {
    if (typeof value !== "number") return value;
    return value.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
  };

  return (
    <div>
      {/* Card widget */}
      <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
        <Widget
          icon={<MdPeople className="h-7 w-7" />}
          title={"Leads"}
          subtitle={loading ? "-" : data?.leadsCount ?? "-"}
          onClick={() => navigate("/admin/lead-management")}
        />
        <Widget
          icon={<MdPeople className="h-7 w-7" />}
          title={"Clients"}
          subtitle={loading ? "-" : data?.clientsCount ?? "-"}
          onClick={() => navigate("/admin/client-management")}
        />
        <Widget
          icon={<MdPeople className="h-7 w-7" />}
          title={"Care Of"}
          subtitle={loading ? "-" : data?.careofCount ?? "-"}
          onClick={() => navigate("/admin/careof-management")}
        />
        <Widget
          icon={<MdAttachMoney className="h-7 w-7" />}
          title={"Total Revenue"}
          subtitle={loading ? "-" : formatINR(data?.totalRevenue)}
        />
        <Widget
          icon={<MdAttachMoney className="h-7 w-7" />}
          title={"Received Amount"}
          subtitle={loading ? "-" : formatINR(data?.totalReceivedAmount)}
        />
        <Widget
          icon={<MdAttachMoney className="h-7 w-7" />}
          title={"Pending Amount"}
          subtitle={loading ? "-" : formatINR(data?.pendingAmount)}
        />
      </div>

      {/* Charts & Loader Overlay */}
      <div className="relative min-h-[900px] rounded-2xl overflow-hidden">
        {loading && <LoaderOverlay />}
        <div className={loading ? 'pointer-events-none select-none opacity-60' : ''}>
          {!loading && data && (
            <>
              <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                <TotalSpent
                  loading={loading}
                  data={data?.monthlyRevenueChart}
                  stats={data?.monthlyRevenueStats}
                />
                <WeeklyRevenue
                  loading={loading}
                  chartData={data?.leadClientStatusChartData}
                />
              </div>

              {/* Tables & Charts */}
              <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                <DailyTraffic
                  loading={loading}
                  chartData={data?.monthlyConversionRateChart}
                />
                <PieChartCard
                  loading={loading}
                  pieData={data?.leadSourcePieChartData}
                />
              </div>

              {/* Hours Charts */}
              <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                <EstimatedHours chartData={data?.estimatedHoursChart} />
                <ActualHours chartData={data?.actualHoursChart} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
